/**
 * Phase 125.30 — Full-Width Rodeo-Promo-Banner mit voll pflegbaren Elementen.
 *
 * Layout inspiriert vom hochgeladenen Design (Sunset-Background, "RODEO"
 * XXL-Titel, Feature-Punkte links, Rodeo-Foto rechts, Logo oben rechts,
 * CTA-Button, untere Icon-Leiste). Jedes Element ist über
 * `settings.home.rodeo_banner` im Admin-CMS editierbar.
 *
 * Config:
 *   {
 *     enabled,
 *     background_image_url,
 *     hero_image_url,
 *     logo_image_url,
 *     badge_label,        // z.B. "Kraft, Action & Spaß!"
 *     title,              // "RODEO"
 *     subtitle,           // "Bist Du bereit für die Challenge?"
 *     features: [ { icon, title, subtitle } × 3 ],
 *     cta_headline,       // "Jetzt buchen!"
 *     cta_subtext,        // "Mach dein Event zum Erlebnis!"
 *     cta_href,
 *     bottom_features: [ { icon, title, subtitle } × 4 ]
 *   }
 */
import React from 'react';
import * as Icons from 'lucide-react';
import ResponsiveImage from './ResponsiveImage';

// Kleiner Icon-Mapper — nur die für diesen Banner sinnvollen Icons.
const IconRenderer = ({ name, className }) => {
  const IconCmp = Icons[name] || Icons.Sparkles;
  return <IconCmp className={className} aria-hidden="true" />;
};

const DEFAULT_BADGE_FONT = '"Anton", "Bebas Neue", sans-serif';
const DEFAULT_TITLE_FONT = '"Carnevalee Freakshow", "Rye", "Anton", sans-serif';
const DEFAULT_SUBTITLE_FONT = '"Permanent Marker", "Caveat Brush", cursive';
const DEFAULT_BADGE_SIZE = 'clamp(0.85rem, 1.4vw, 1.1rem)';
const DEFAULT_TITLE_SIZE = 'clamp(2.75rem, 7.4vw, 6rem)';
const DEFAULT_SUBTITLE_SIZE = 'clamp(1.4rem, 3.2vw, 2.2rem)';

const DEFAULTS = {
  background_image_url:
    'https://customer-assets-jt897jd0.emergentagent.net/job_34531605-61c4-451d-9ee7-b24843ceca0d/artifacts/55cnlqbk_ChatGPT%20Image%2019.%20Aug.%202026%2C%2010_56_51.png',
  hero_image_url: '',
  logo_image_url: '',
  badge_label: 'Kraft, Action & Spaß!',
  badge_font_family: '',
  badge_font_size: '',
  title: 'BULLRIDING',
  title_font_family: '',
  title_font_size: '',
  subtitle: 'Bist Du bereit für die Challenge?',
  subtitle_font_family: '',
  subtitle_font_size: '',
  features: [
    { icon: 'Zap', title: 'Wilder Spaß für', subtitle: 'Kinder, Teens & Erwachsene' },
    { icon: 'Star', title: 'Das Highlight auf jedem Event —', subtitle: 'Firmenfeiern, Stadtfeste & mehr' },
    { icon: 'ShieldCheck', title: 'Sicher & zuverlässig', subtitle: 'aufgebaut & betreut' },
  ],
  cta_headline: 'Jetzt buchen!',
  cta_subtext: 'Mach Dein Event zum Erlebnis!',
  cta_href: '/produkte?kategorie=Attraktionen',
  bottom_features: [
    { icon: 'Users', title: 'Für Klein & Groß', subtitle: '' },
    { icon: 'PartyPopper', title: 'Perfekt für Events', subtitle: 'aller Art' },
    { icon: 'ShieldCheck', title: 'Sicher & Robust', subtitle: 'konstruiert' },
    { icon: 'CalendarCheck', title: 'Flexibel Buchbar', subtitle: 'für Deinen Termin' },
  ],
};

const HomeRodeoBanner = ({ config, topBg, bottomBg, wrapperBg }) => {
  const raw = config || {};
  const cfg = { ...DEFAULTS, ...raw };
  if (raw.enabled === false) return null;

  const features = Array.isArray(cfg.features) && cfg.features.length ? cfg.features : DEFAULTS.features;
  const topFill = topBg === null ? null : (topBg || wrapperBg || '#ffffff');
  const bottomFill = bottomBg === null ? null : (bottomBg || wrapperBg || '#ffffff');

  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-rodeo-banner"
    >
      {/* Hintergrund: Sunset-Bild */}
      <div
        className="relative w-full"
        style={{
          backgroundImage: `url("${cfg.background_image_url}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dunkler Farbverlauf für Lesbarkeit */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.35) 100%)',
          }}
        />

        {/* Top wave (Wrapper-Farbe fließt in den Banner) */}
        {/* Top wave (Wrapper-Farbe fließt in den Banner) */}
        {topFill && (
          <svg
            className="absolute left-0 w-full h-8 md:h-12 block pointer-events-none z-20"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            style={{ display: 'block', transform: 'rotate(180deg)', top: '-2px' }}
            aria-hidden="true"
          >
            <path
              fill={topFill}
              d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
            />
          </svg>
        )}

        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-14 md:pb-20 z-10">
          {/* SVG-Grunge-Filter — stärkere Displacement für den rauen Western-Look */}
          <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
            <defs>
              <filter id="rodeo-grunge">
                <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="7" />
                <feDisplacementMap in="SourceGraphic" scale="7" />
              </filter>
            </defs>
          </svg>
          {/* Logo oben rechts */}
          {cfg.logo_image_url && (
            <div className="absolute top-4 right-4 md:top-6 md:right-6 w-20 h-20 md:w-28 md:h-28 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm ring-2 ring-white/30 shadow-xl">
              <ResponsiveImage
                src={cfg.logo_image_url}
                alt="Logo"
                className="w-full h-full object-contain p-1.5"
              />
            </div>
          )}

          <div className={cfg.hero_image_url ? 'grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-6 md:gap-8 items-center' : 'max-w-3xl'}>
            {/* LEFT — Text-Block */}
            <div className="text-white space-y-4 md:space-y-5">
              <div
                className="inline-block bg-sky-800/95 text-white px-5 py-1.5 rounded-md shadow-lg uppercase"
                style={{
                  fontFamily: cfg.badge_font_family || DEFAULT_BADGE_FONT,
                  fontSize: cfg.badge_font_size || DEFAULT_BADGE_SIZE,
                  letterSpacing: '0.05em',
                  transform: 'rotate(-2deg) skew(-8deg)',
                }}
                data-testid="rodeo-badge"
              >
                {cfg.badge_label}
              </div>
              <div
                role="heading"
                aria-level="2"
                className="leading-none text-white drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)]"
                style={{
                  fontFamily: cfg.title_font_family || DEFAULT_TITLE_FONT,
                  fontSize: cfg.title_font_size || DEFAULT_TITLE_SIZE,
                  letterSpacing: '0.02em',
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  transform: 'skew(-4deg)',
                  transformOrigin: 'left center',
                }}
                data-testid="rodeo-title"
              >
                {cfg.title}
              </div>
              <div
                className="text-yellow-400 leading-[1.05] drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]"
                style={{
                  fontFamily: cfg.subtitle_font_family || DEFAULT_SUBTITLE_FONT,
                  fontSize: cfg.subtitle_font_size || DEFAULT_SUBTITLE_SIZE,
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  transform: 'skew(-6deg)',
                  transformOrigin: 'left center',
                  letterSpacing: '0.01em',
                  maxWidth: '18ch',
                }}
                data-testid="rodeo-subtitle"
              >
                {cfg.subtitle}
              </div>

              {/* Feature-Liste */}
              <ul className="space-y-2.5 pt-1">
                {features.slice(0, 3).map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-sky-900/80 border-2 border-yellow-400 flex items-center justify-center shadow-lg">
                      <IconRenderer name={f.icon} className="h-4 w-4 md:h-4.5 md:w-4.5 text-yellow-400" />
                    </div>
                    <div
                      className="text-white text-sm md:text-base leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]"
                      style={{ fontFamily: 'inherit' }}
                    >
                      <div className="font-semibold">{f.title}</div>
                      {f.subtitle && (
                        <div className="text-white/85 font-normal">{f.subtitle}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {/* CTA-Button direkt unter der Feature-Liste (linke Spalte) */}
              <div className="pt-4">
                <a
                  href={cfg.cta_href}
                  className="inline-flex flex-col items-center bg-red-700 hover:bg-red-600 text-white px-5 md:px-7 py-2 md:py-3 rounded-md shadow-2xl transition-transform hover:scale-105"
                  style={{ transform: 'rotate(-1deg)' }}
                  data-testid="rodeo-cta"
                >
                  <span
                    className="font-black uppercase tracking-wide leading-none"
                    style={{ fontSize: 'clamp(1rem, 1.9vw, 1.3rem)' }}
                  >
                    {cfg.cta_headline}
                  </span>
                  {cfg.cta_subtext && (
                    <span className="text-yellow-300 font-bold uppercase text-[9px] md:text-[11px] tracking-wider mt-1">
                      {cfg.cta_subtext}
                    </span>
                  )}
                </a>
              </div>
            </div>

            {/* RIGHT — Rodeo-Foto (nur anzeigen, wenn im CMS ein Foto hinterlegt ist) */}
            {cfg.hero_image_url && (
              <div className="relative">
                <ResponsiveImage
                  src={cfg.hero_image_url}
                  alt="Rodeo Bullriding"
                  className="w-full h-auto max-h-[420px] object-contain drop-shadow-2xl"
                />
              </div>
            )}
          </div>
        </div>

        {/* Bottom wave (Slider fließt zurück in die Wrapper-Farbe) */}
        {bottomFill && (
          <svg
            className="absolute left-0 w-full h-8 md:h-12 block pointer-events-none z-20"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            style={{ display: 'block', bottom: '-2px' }}
            aria-hidden="true"
          >
            <path
              fill={bottomFill}
              d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
            />
          </svg>
        )}
      </div>
    </section>
  );
};

export default HomeRodeoBanner;
